'use client';

import React, { useMemo } from 'react';
import AppLogo from '@/components/ui/AppLogo';
import { MapPin, Phone, Mail } from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useLanguage } from '@/contexts/LanguageContext';
import { marketingLangFromPathname } from '@/lib/i18n/marketing-path';
import { BrandText } from '@/components/marketing/BrandText';

export default function PublicFooter() {
  const { t } = useLanguage();
  const pathname = usePathname() ?? '/';
  const pathLang = marketingLangFromPathname(pathname);
  const homeBase = `/${pathLang}/home`;

  const FOOTER_SERVICES = useMemo(
    () => [
      { labelKey: 'nav.blog', href: `/${pathLang}/blog` },
      { labelKey: 'nav.virtualOffice', href: `${homeBase}#virtual-office` },
      { labelKey: 'footer.businessAddress', href: `${homeBase}#virtual-office` },
      { labelKey: 'nav.coworking', href: `${homeBase}#coworking` },
      { labelKey: 'nav.accounting', href: `${homeBase}#accounting` },
      { labelKey: 'footer.legalAssistance', href: `${homeBase}#accounting` },
      { labelKey: 'footer.companyRegistration', href: `${homeBase}#virtual-office` },
    ],
    [pathLang, homeBase],
  );

  const FOOTER_COMPANY = useMemo(
    () => [
      { labelKey: 'footer.about', href: `${homeBase}#trust` },
      { labelKey: 'footer.howItWorks', href: `${homeBase}#how-it-works` },
      { labelKey: 'footer.pricing', href: `${homeBase}#virtual-office` },
      { labelKey: 'footer.clientPortal', href: '/client-portal' },
      { labelKey: 'nav.contact', href: `${homeBase}#contact` },
    ],
    [homeBase],
  );

  return (
    <footer className="bg-[#1B3A5C] text-white">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-4">
              <AppLogo surface="dark" size={48} />
            </div>
            <p className="text-slate-300 text-sm leading-relaxed mb-6">
              <BrandText text={t('footer.desc')} />
            </p>
            
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display font-semibold text-white text-sm uppercase tracking-wider mb-4">{t('footer.services')}</h4>
            <ul className="space-y-2.5">
              {FOOTER_SERVICES?.map((item, i) => (
                <li key={`footer-svc-${i}`}>
                  <a href={item?.href} className="text-slate-300 text-sm hover:text-white transition-colors duration-150">
                    {t(item?.labelKey)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="font-display font-semibold text-white text-sm uppercase tracking-wider mb-4">{t('footer.company')}</h4>
            <ul className="space-y-2.5">
              {FOOTER_COMPANY?.map((item) => (
                <li key={`footer-co-${item?.labelKey}`}>
                  <Link href={item?.href} className="text-slate-300 text-sm hover:text-white transition-colors duration-150">
                    {item?.labelKey === 'footer.about' ? (
                      <BrandText text={t('footer.about')} />
                    ) : (
                      t(item?.labelKey)
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-display font-semibold text-white text-sm uppercase tracking-wider mb-4">{t('footer.contact')}</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2.5">
                <MapPin size={15} className="text-[#C9A96E] mt-0.5 shrink-0" />
                <span className="text-slate-300 text-sm leading-relaxed">
                  {t('contact.addressValue')}
                </span>
              </li>
              <li className="flex items-center gap-2.5">
                <Phone size={15} className="text-[#C9A96E] shrink-0" />
                <a href="tel:+38162262221" className="text-slate-300 text-sm hover:text-white transition-colors duration-150">
                  {t('contact.phoneValue')}
                </a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail size={15} className="text-[#C9A96E] shrink-0" />
                <a href="mailto:office@top14.rs" className="text-slate-300 text-sm hover:text-white transition-colors duration-150">
                  {t('contact.emailValue')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-400 text-sm">
            <BrandText text={t('footer.rights')} />
          </p>
          <div className="flex items-center gap-5 flex-wrap justify-center sm:justify-end">
            <Link href={`/${pathLang}/legal/privacy`} className="text-slate-400 text-sm hover:text-white transition-colors duration-150">
              {t('footer.privacy')}
            </Link>
            <Link href={`/${pathLang}/legal/terms`} className="text-slate-400 text-sm hover:text-white transition-colors duration-150">
              {t('footer.terms')}
            </Link>
            <Link href={`/${pathLang}/legal/cookies`} className="text-slate-400 text-sm hover:text-white transition-colors duration-150">
              {t('footer.cookies')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}